// src/components/Sidebar/SidebarProjectList.jsx
import React, { useMemo } from "react";
import SidebarProjectItem from "./SidebarProjectItem";
import SidebarSkeleton from "./SidebarSkeleton";
import { useVirtualList } from "./useVirtualList";

const ITEM_HEIGHT = 40; // px, keep in sync with .Sidebar-subitem min-height + gap in SCSS
const VIRTUALIZE_THRESHOLD = 40; // below this, plain rendering is simpler and just as fast

export default function SidebarProjectList({
  projects,
  loading,
  activeProjectId,
  statusMap,
  collapsed,
  onSelect,
}) {
  const shouldVirtualize = projects.length > VIRTUALIZE_THRESHOLD;

  const { containerRef, range, totalHeight } = useVirtualList({
    count: shouldVirtualize ? projects.length : 0,
    itemHeight: ITEM_HEIGHT,
    overscan: 8,
  });

  const visibleProjects = useMemo(() => {
    if (!shouldVirtualize) return projects;
    return projects.slice(range.start, range.end);
  }, [shouldVirtualize, projects, range]);

  if (loading) {
    return (
      <div className="Sidebar-subitems" aria-busy="true" aria-label="Loading projects">
        <SidebarSkeleton rows={6} />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="Sidebar-subitems">
        <div className="Sidebar-empty-state" role="status">
          <p className="Sidebar-empty-title">No projects yet</p>
          {!collapsed && (
            <p className="Sidebar-empty-subtitle">
              Projects you create or get added to will show up here.
            </p>
          )}
        </div>
      </div>
    );
  }

  // This div is the ONE designated scroll container for the project
  // list. It's a normal block element with native overflow-y: auto —
  // wheel, touchpad, keyboard (Space/Arrow/Page keys once focused),
  // and touch scrolling all work without any custom event handling.
  return (
    <div
      ref={containerRef}
      className="Sidebar-subitems"
      role="listbox"
      aria-label="Projects"
      tabIndex={0}
    >
      {shouldVirtualize ? (
        <div className="Sidebar-virtual-spacer" style={{ height: totalHeight }}>
          {visibleProjects.map((p, i) => {
            const index = range.start + i;
            return (
              <SidebarProjectItem
                key={p.projectId}
                project={p}
                active={activeProjectId === String(p.projectId)}
                status={statusMap.get(p.projectId)}
                collapsed={collapsed}
                onSelect={onSelect}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  transform: `translateY(${index * ITEM_HEIGHT}px)`,
                  height: ITEM_HEIGHT - 6,
                }}
              />
            );
          })}
        </div>
      ) : (
        projects.map((p) => (
          <SidebarProjectItem
            key={p.projectId}
            project={p}
            active={activeProjectId === String(p.projectId)}
            status={statusMap.get(p.projectId)}
            collapsed={collapsed}
            onSelect={onSelect}
          />
        ))
      )}
    </div>
  );
}
